import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { Copy, Download, FileUp } from "lucide-react";
import "./CanvasMode.css";
import {
  selectCanvasModeIsOpen,
  selectCanvasModeMessage,
  selectCanvasModeUI,
  closeCanvasMode,
  showUnsavedChangesModal,
  hideUnsavedChangesModal,
  updateCanvasContent,
  updateCanvasTitle,
  setCanvasMessage,
  setCanvasMessageSaved,
  setCanvasAutoSaving,
  selectEmployeeProfileSidebarIsOpen,
  closeEmployeeProfileSidebar,
  setMessages,
  selectMessages,
} from "../../redux/features/agent/agentSlice";
import { CanvasEditor } from "./CanvasEditor";
import { ModalCanvasUnsavedChanges } from "./ModalCanvasUnsavedChanges";
import { ModalSaveToDocuments } from "./ModalSaveToDocuments";
import { useCanvasExport } from "./hooks/useCanvasExport";
import { CanvasModeService } from "../../services/CanvasModeService";
import { MessageType, AIResponseCanvasMessage, LinkRecord } from "winslow-core";
import { scrubTitleForDocument, isTitleValidForSaving } from "./utils";

import { MyTooltip } from "../common/MyTooltip";
import { CanvasMessage } from "./types";
import { AnswerSidebarResource } from "../collections/HomeScreen/AnswerSidebarResource";
import { Icon } from "../../ds2/components/Icon";

const AUTO_SAVE_DELAY_MS = 2000;

export const CanvasMode: React.FC<{}> = () => {
  const dispatch = useDispatch();
  const isCanvasModeOpen = useSelector(selectCanvasModeIsOpen);
  const canvasMessage = useSelector(selectCanvasModeMessage);
  const canvasUI = useSelector(selectCanvasModeUI);
  const isEmployeeProfileOpen = useSelector(selectEmployeeProfileSidebarIsOpen);
  const messages = useSelector(selectMessages);
  const [showSaveToDocumentsDialog, setShowSaveToDocumentsDialog] =
    useState(false);
  const autoSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [hasEditedSinceOpen, setHasEditedSinceOpen] = useState(false);
  const [linkedResource, setLinkedResource] = useState<LinkRecord | null>(null);

  const {
    exportPdf,
    saveToResources,
    saveToDocuments,
    copyAsRTF,
    generateTitle,
  } = useCanvasExport();

  useEffect(() => {
    dispatch(closeCanvasMode());
  }, [dispatch]);

  const sourceMessage = useMemo(
    () => messages.find((msg) => msg.id === canvasMessage?.id) || null,
    [messages, canvasMessage?.id]
  );

  useEffect(() => {
    if (!canvasMessage) {
      setLinkedResource(null);
      return;
    }

    let active = true;

    CanvasModeService.getCanvasResource(canvasMessage.id)
      .then((record) => {
        if (!active) return;
        setLinkedResource(record);

        if (record && canvasMessage.linkRecordId !== record.id) {
          dispatch(
            setCanvasMessage({
              ...canvasMessage,
              linkRecordId: record.id,
            } as CanvasMessage)
          );
        }
      })
      .catch(() => {
        if (!active) return;
        setLinkedResource(null);
      });

    return () => {
      active = false;
    };
  }, [canvasMessage?.id, dispatch]);

  // Close Employee Profile when Canvas Mode opens (mutual exclusivity)
  useEffect(() => {
    if (isCanvasModeOpen && isEmployeeProfileOpen) {
      dispatch(closeEmployeeProfileSidebar());
    }
  }, [isCanvasModeOpen, isEmployeeProfileOpen, dispatch]);

  // Body class management for layout adjustments
  useEffect(() => {
    if (isCanvasModeOpen) {
      document.body.classList.add("canvas-mode-open");
    } else {
      document.body.classList.remove("canvas-mode-open");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("canvas-mode-open");
    };
  }, [isCanvasModeOpen]);

  // Initialize canvas mode when opened
  // useEffect(() => {
  //   if (
  //     isCanvasModeOpen &&
  //     canvasMessage &&
  //     (!canvasMessage.message.title || "Untitled Document")
  //   ) {
  //     // Generate an AI title for the document
  //     generateTitle(
  //       canvasMessage.id,
  //       canvasMessage.message.content,
  //       canvasMessage.message.title || ""
  //     ).then((generatedTitle) => {
  //       dispatch(updateCanvasTitle(generatedTitle || "Untitled Document"));
  //     });
  //   }
  // }, [isCanvasModeOpen, canvasMessage, dispatch, generateTitle]);

  // Handle closing canvas mode
  // Handle saving document (update canvas message)
  const handleSave = useCallback(async () => {
    if (
      !canvasMessage ||
      !canvasMessage.hasChanges ||
      canvasMessage.isAutoSaving
    ) {
      return;
    }

    dispatch(setCanvasAutoSaving(true));

    try {
      const updatedMessage = await CanvasModeService.updateCanvasMessage({
        id: canvasMessage.id,
        title: canvasMessage.message.title,
        content: canvasMessage.message.content,
      });

      if (updatedMessage) {
        const updatedMessages = messages.map((msg) => {
          if (msg.id === canvasMessage.id) {
            return {
              ...msg,
              type: MessageType.AI_CANVAS_MESSAGE,
              message: {
                title: canvasMessage.message.title,
                content: canvasMessage.message.content,
              },
              updatedAt: new Date(),
            } as AIResponseCanvasMessage;
          }
          return msg;
        });

        dispatch(setMessages(updatedMessages));
        dispatch(setCanvasMessageSaved());
      }
    } catch (error) {
      console.error("Failed to update canvas message:", error);
    } finally {
      dispatch(setCanvasAutoSaving(false));
    }
  }, [canvasMessage, messages, dispatch]);

  const handleClose = useCallback(() => {
    if (!canvasMessage) {
      dispatch(closeCanvasMode());
      setHasEditedSinceOpen(false);
      return;
    }

    if (canvasMessage.hasChanges) {
      void handleSave();
    }

    if (canvasMessage.linkRecordId && hasEditedSinceOpen) {
      dispatch(showUnsavedChangesModal());
      return;
    }

    setHasEditedSinceOpen(false);
    dispatch(closeCanvasMode());
  }, [
    canvasMessage,
    dispatch,
    handleSave,
    hasEditedSinceOpen,
    setHasEditedSinceOpen,
  ]);

  // Handle content changes from editor
  const handleContentChange = useCallback(
    (content: string) => {
      setHasEditedSinceOpen(true);
      dispatch(updateCanvasContent(content));
    },
    [dispatch]
  );

  // Handle title changes
  const handleTitleChange = useCallback(
    (title: string) => {
      setHasEditedSinceOpen(true);
      dispatch(updateCanvasTitle(title));
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isCanvasModeOpen || !canvasMessage) {
      setHasEditedSinceOpen(false);
      return;
    }

    setHasEditedSinceOpen(false);
  }, [isCanvasModeOpen, canvasMessage?.id]);

  // Debounced auto-save whenever content or title changes
  useEffect(() => {
    if (!canvasMessage) {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
      return;
    }

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = null;
    }

    if (!canvasMessage.hasChanges || canvasMessage.isAutoSaving) {
      return;
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = null;
      handleSave();
    }, AUTO_SAVE_DELAY_MS);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = null;
      }
    };
  }, [
    canvasMessage?.id,
    canvasMessage?.message.content,
    canvasMessage?.message.title,
    canvasMessage?.hasChanges,
    canvasMessage?.isAutoSaving,
    handleSave,
  ]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isCanvasModeOpen) return;

      // Ctrl/Cmd + S to save (prevent browser save)
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      }

      // Escape to close (with unsaved changes check)
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isCanvasModeOpen, handleClose, handleSave]);

  // Handle export to PDF
  const handleExportPdf = useCallback(async () => {
    if (canvasMessage) {
      await exportPdf(
        canvasMessage.message.title || "Untitled Document",
        canvasMessage.message.content,
        canvasMessage.message.title
      );
    }
  }, [canvasMessage, exportPdf]);

  // Handle save to resources
  const handleSaveToResources = useCallback(async () => {
    if (!canvasMessage) {
      return false;
    }

    const scrubbedTitle = scrubTitleForDocument(canvasMessage.message.title);
    const result = await saveToResources(canvasMessage.id, scrubbedTitle);

    if (result?.success) {
      setHasEditedSinceOpen(false);

      if (result.linkRecordId) {
        const linkRecordId = result.linkRecordId;
        dispatch(
          setCanvasMessage({
            ...canvasMessage,
            linkRecordId,
          } as CanvasMessage)
        );

        const updatedMessages = messages.map((msg) => {
          if (msg.id === canvasMessage.id) {
            return {
              ...msg,
              sources: [linkRecordId],
            };
          }
          return msg;
        });

        dispatch(setMessages(updatedMessages));

        CanvasModeService.getCanvasResource(canvasMessage.id).then((record) => {
          if (record) {
            setLinkedResource(record);
          }
        });
      }
    }

    return result?.success ?? false;
  }, [canvasMessage, dispatch, messages, saveToResources]);

  // Handle save to documents - show dialog
  const handleSaveToDocuments = useCallback(() => {
    setShowSaveToDocumentsDialog(true);
  }, []);

  // Handle actual save to documents with title and description
  const handleSaveToDocumentsWithDetails = useCallback(
    async (title: string, description: string) => {
      if (canvasMessage) {
        const scrubbedTitle = scrubTitleForDocument(title);
        await saveToDocuments(canvasMessage.id, scrubbedTitle, description);
        setShowSaveToDocumentsDialog(false);
      }
    },
    [canvasMessage, saveToDocuments]
  );

  // Handle copy as RTF
  const handleCopyAsRTF = useCallback(async () => {
    if (canvasMessage) {
      await copyAsRTF(canvasMessage.message.content);
    }
  }, [canvasMessage, copyAsRTF]);

  // Handle unsaved changes modal actions
  const handleModalSave = useCallback(async () => {
    const success = await handleSaveToResources();
    if (success) {
      dispatch(hideUnsavedChangesModal());
      dispatch(closeCanvasMode());
    }
  }, [handleSaveToResources, dispatch]);

  const handleModalDiscard = useCallback(() => {
    dispatch(hideUnsavedChangesModal());
    setHasEditedSinceOpen(false);
    dispatch(closeCanvasMode());
  }, [dispatch, setHasEditedSinceOpen]);

  const handleModalCancel = useCallback(() => {
    dispatch(hideUnsavedChangesModal());
  }, [dispatch]);

  // Don't render if not open
  if (!isCanvasModeOpen || !canvasMessage) {
    return null;
  }

  const resourceLink = linkedResource?.links?.[0] || null;
  const resourceCollectionId =
    linkedResource?.collectionId ||
    (sourceMessage as any)?.thread?.collectionId ||
    (sourceMessage as any)?.thread?.collection?.id ||
    "";

  return (
    <>
      <div
        className={`canvas-mode-sidebar ${isCanvasModeOpen ? "canvas-mode-open" : ""}`}>
        {/* Action buttons with close button */}
        <div className="canvas-mode-actions">
          <div className="canvas-mode-action-group">
            <div className="canvas-mode-action-group-left">
              {canvasMessage.linkRecordId &&
                resourceLink &&
                resourceCollectionId && (
                  <div className="canvas-mode-resource-banner">
                    <AnswerSidebarResource
                      link={resourceLink}
                      collectionId={resourceCollectionId}
                      minimum
                      showDrag={false}
                      showAIMenu={false}
                      allowContextMenu={false}
                      clickIsOpen
                    />
                  </div>
                )}
            </div>

            <div className="canvas-mode-action-group-right">
              <MyTooltip title="Copy">
                <Icon
                  icon={<Copy size={20} />}
                  variant="secondary"
                  border={true}
                  onClick={handleCopyAsRTF}
                  disabled={!canvasMessage}
                />
              </MyTooltip>

              <MyTooltip
                title={
                  !isTitleValidForSaving(canvasMessage?.message.title || "")
                    ? "Please set a valid title before saving"
                    : "Save to Resources"
                }>
                <Icon
                  icon={<FileUp size={20} />}
                  variant="secondary"
                  border={true}
                  onClick={handleSaveToResources}
                  disabled={
                    canvasUI.isSavingToResources ||
                    !canvasMessage ||
                    !isTitleValidForSaving(canvasMessage?.message.title || "")
                  }
                />
              </MyTooltip>

              <MyTooltip title="Export PDF">
                <Icon
                  icon={<Download size={20} />}
                  variant="secondary"
                  border={true}
                  onClick={handleExportPdf}
                  disabled={canvasUI.isExporting || !canvasMessage}
                />
              </MyTooltip>
            </div>

            {/* <Button
              variant="tertiary"
              size="sm"
              onClick={handleClose}
              title="Close Canvas Mode (Esc)">
              ×
            </Button> */}

            {/* NR> DO NO CONVERT THIS TO BUTTON */}
            <button
              className="canvas-mode-close-button"
              onClick={handleClose}
              title="Close Canvas Mode (Esc)">
              ×
            </button>
          </div>
        </div>

        {/* Canvas Editor */}
        <div className="canvas-mode-editor">
          <CanvasEditor
            content={canvasMessage.message.content}
            title={canvasMessage.message.title}
            isLoading={canvasMessage.isAutoSaving}
            onChange={handleContentChange}
            onTitleChange={handleTitleChange}
            hasUnsavedChanges={canvasMessage.hasChanges}
          />
        </div>
      </div>

      {/* Unsaved changes modal */}
      <ModalCanvasUnsavedChanges
        isOpen={canvasUI.showUnsavedChangesModal}
        onSave={handleModalSave}
        onDiscard={handleModalDiscard}
        onCancel={handleModalCancel}
      />

      {/* Save to documents dialog */}
      <ModalSaveToDocuments
        isOpen={showSaveToDocumentsDialog}
        onClose={() => setShowSaveToDocumentsDialog(false)}
        onSave={handleSaveToDocumentsWithDetails}
        isLoading={canvasUI.isSavingToDocuments}
        defaultTitle={canvasMessage?.message.title || ""}
      />
    </>
  );
};
